'use client';

import { listByProductIds } from '@/api/public';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Banner from './components/Banner';
import { usePersonalityFitTracking } from './hooks/usePersonalityFitTracking';
import type { PersonalityFitClientProps } from './types';
import WapPhotoWall from './wapComponents/PhotoWall';
import WapProductLists from './wapComponents/ProductLists';
import WapWaterFull from './wapComponents/WaterFull';

export default function PersonalityFitWapClient({ initialData }: PersonalityFitClientProps) {
  const [pageData, setPageData] = useState<any>(initialData || {});
  const { navigate, inAppH5 } = useAppNavigation();
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();
  const [bottomRef, bottomInView] = useInView({ threshold: 0.5, triggerOnce: true });

  // 底部按钮区域曝光
  if (bottomInView && (pageData as any)?.bottom_part?.jump_id) {
    try {
      trackMktExp({
        mkt_name: 'Waterfall_Area_1_Btn',
        group_id: String((pageData as any)?.bottom_part?.jump_id),
      });
    } catch {
      /* noop */
    }
  }

  const handleViewMoreClick = (jumpId?: string | number) => {
    if (!jumpId) return;
    if (!inAppH5 && typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
    }
    try {
      trackMktClick({ mkt_name: 'Waterfall_Area_1_Btn', group_id: String(jumpId) });
    } catch {
      /* noop */
    }
    navigate(`/goods-list/${jumpId}`);
  };

  // 客户端兜底合并：对齐 Vue getProductData 的行为（WAP 同步）
  useEffect(() => {
    const mergeByIds = async (arr: any[], opts?: { noSoldOut?: boolean }) => {
      const ids: number[] = Array.isArray(arr)
        ? (arr as any[]).filter((i: any) => i && i.id).map((i: any) => Number(i.id))
        : [];
      if (!ids.length) return arr;
      try {
        const params: any = { product_ids: ids.join(',') };
        if (opts?.noSoldOut) {
          params.in_stock = 1;
          params.is_sale = 1;
        }
        const resp: any = await listByProductIds(params);
        const list: any[] = Array.isArray(resp?.list) ? resp.list : [];
        if (!list.length) return arr;
        return (arr as any[]).map((item: any) => {
          if (!item?.id) return item;
          const found = list.find((x: any) => Number(x?.id) === Number(item?.id));
          return found ? { ...found, ...item } : item;
        });
      } catch {
        return arr;
      }
    };

    (async () => {
      if (Array.isArray((initialData as any)?.product_lists)) {
        const merged = await mergeByIds((initialData as any).product_lists);
        if (merged !== (initialData as any).product_lists) {
          setPageData((prev: any) => ({ ...prev, product_lists: merged }));
        }
      }
      if (Array.isArray((initialData as any)?.waterFull)) {
        const mergedWF = await mergeByIds((initialData as any).waterFull, { noSoldOut: true });
        if (mergedWF !== (initialData as any).waterFull) {
          setPageData((prev: any) => ({ ...prev, waterFull: mergedWF }));
        }
      }
    })();
  }, [initialData]);
  return (
    <div className="personality-fit-wap bg-black">
      {pageData?.banner1 && <Banner bannerData={(pageData as any).banner1} bannerIndex={0} />}
      {pageData?.banner2 && (
        <Banner bannerData={(pageData as any).banner2} bannerIndex={1} className="h-20" />
      )}
      {Array.isArray((pageData as any).product_lists) &&
        (pageData as any).product_lists.length > 0 && (
          <WapProductLists productLists={(pageData as any).product_lists} />
        )}
      {/* PhotoWall - 对齐 PC 端 */}
      {(() => {
        const part = (pageData as any)?.swiper_part || {};
        const lists =
          part?.swiper_lists ||
          (pageData as any)?.swiper_lists ||
          (pageData as any)?.swiperLists ||
          [];
        const bgImg =
          part?.bg_img || (pageData as any)?.swiper_banner || (pageData as any)?.swiperBanner || '';
        if (Array.isArray(lists) && lists.length > 0) {
          return <WapPhotoWall swiperPart={{ swiper_lists: lists, bg_img: bgImg }} />;
        }
        return null;
      })()}
      {Array.isArray((pageData as any).waterFull) && (pageData as any).waterFull.length > 0 && (
        <WapWaterFull
          waterFull={(pageData as any).waterFull}
          bannerThreeData={(pageData as any).banner3}
        />
      )}

      {/* Bottom View More */}
      {(pageData as any)?.bottom_part?.jump_id && (
        <div
          ref={bottomRef}
          className="bottom-part flex flex-col items-center justify-center px-[14px] pb-[30px]"
        >
          <a
            href={`/goods-list/${(pageData as any).bottom_part.jump_id}`}
            className="banner-wrap mb-[30px] block"
            onClick={(e) => {
              e.preventDefault();
              handleViewMoreClick((pageData as any).bottom_part.jump_id);
            }}
          >
            <div className="btn w-[200px] h-[35px] rounded-[18px] border border-[#BDAD8D] box-border text-center leading-[35px] text-[15px] text-[#BDAD8D] font-bb">
              {(pageData as any).bottom_part?.button_word}
            </div>
          </a>
          {(pageData as any).bottom_part?.description && (
            <p className="word text-center text-[#BDAD8D] text-[12px] leading-[16px]">
              {(pageData as any).bottom_part.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
